import { Sheet, SheetStack } from "@silk-hq/components"
import PropTypes from "prop-types"
import React, { Fragment, useEffect } from "react"

const ModalMobile = ({
  onClose,
  children,
  isOpen,
  afterLeave,
  initialFocusRef,
  stacked,
}) => {
  // stacked={true} means this modal is a child on top of another modal.
  // In that case, omit SheetStack.Root so this sheet participates in the
  // parent modal's stack context (triggering the iOS scale-back animation).
  // stacked={false} (default) means this is a root-level modal — it wraps
  // with SheetStack.Root so any future stacked child can participate in it.
  const Wrapper = stacked ? Fragment : SheetStack.Root

  // The Intercom launcher is fixed bottom-right and overlaps the sheet's
  // footer buttons while a full-screen modal is presented. Snapshot the
  // prior value so we don't reveal the launcher in contexts that had it
  // hidden already (e.g., PWA standalone mode, in-app Chat panel).
  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return undefined

    const previousHidden = window.intercomSettings?.hide_default_launcher
    window.intercomSettings = {
      ...window.intercomSettings,
      hide_default_launcher: true,
    }
    window.Intercom?.("update", window.intercomSettings)

    return () => {
      window.intercomSettings = {
        ...window.intercomSettings,
        hide_default_launcher: previousHidden ?? false,
      }
      window.Intercom?.("update", window.intercomSettings)
    }
  }, [isOpen])

  return (
    <Wrapper>
      <Sheet.Root
        license="commercial"
        presented={isOpen}
        onPresentedChange={(open) => {
          if (!open) onClose()
        }}
      >
        <Sheet.Portal>
          <Sheet.View
            className={stacked ? "z-modal-stacked" : "z-modal"}
            contentPlacement="bottom"
            swipe={false}
            nativeEdgeSwipePrevention
            onEscapeKeyDown={{ dismiss: true }}
            onClickOutside={{ dismiss: true }}
            onTravelStatusChange={(status) => {
              if (status === "idleOutside") afterLeave?.()
              if (status === "idleInside") initialFocusRef?.current?.focus()
            }}
            style={{
              height: "calc(100lvh + 60px)",
              top: "calc(env(safe-area-inset-top, 0px) - 1px)",
            }}
          >
            <Sheet.Backdrop className="fixed inset-0 bg-black/50" />
            <Sheet.Content className="flex h-full w-full flex-col bg-white shadow-xl">
              {/* Constrain to the visible viewport. dvh (not lvh) tracks the
                  current viewport so the footer stays reachable on Android
                  Chrome and iOS Safari while the URL bar is showing. +1px
                  corrects the -1px top offset on Sheet.View so the wrapper
                  fills exactly the screen. */}
              <div
                className="flex flex-col overflow-hidden"
                style={{
                  height: "calc(100dvh - env(safe-area-inset-top, 0px) + 1px)",
                }}
              >
                <div className="flex h-12 shrink-0 items-center justify-end border-b border-gray-100 px-4">
                  <Sheet.Trigger
                    action="dismiss"
                    className="bg-transparent text-2xl font-semibold text-gray-700 hover:text-gray-500 focus:outline-none focus:ring"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </Sheet.Trigger>
                </div>
                <div className="flex flex-1 flex-col overflow-hidden p-4">
                  {children}
                </div>
              </div>
            </Sheet.Content>
          </Sheet.View>
        </Sheet.Portal>
      </Sheet.Root>
    </Wrapper>
  )
}

ModalMobile.propTypes = {
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  afterLeave: PropTypes.func,
  initialFocusRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  stacked: PropTypes.bool,
}

export default ModalMobile
